import { FirstSessionPromise } from "@/components/site/FirstSessionPromise";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Sparkles,
  MessageSquare,
  CheckCircle2,
  ShieldCheck,
  ArrowRight,
  Target,
  Clock,
  Headphones,
  ClipboardCheck,
} from "lucide-react";
import { site } from "@/lib/site";
import { openPlanChat } from "@/lib/plans";

export const Route = createFileRoute("/basic-support")({
  head: () => ({
    meta: [
      { title: "Basic Support - US Visa Mock Interview Practice | WeLivdIt" },
      {
        name: "description",
        content:
          "Already confident? 2 focused 1-on-1 mock interviews with real-time feedback to sharpen delivery before your US visa interview. ₹3,000.",
      },
      { name: "keywords", content: "US visa mock interview, F1 mock practice, visa interview practice, confident applicant coaching" },
      { property: "og:title", content: "Basic Support - US Visa Mock Interview Practice" },
      {
        property: "og:description",
        content: "2 one-on-one mocks with feedback for confident applicants who just need practice. ₹3,000.",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://welivdit.com/basic-support" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/basic-support" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "US Visa Basic Support",
          provider: { "@type": "Organization", name: "WeLivdIt" },
          serviceType: "US visa mock interview practice for confident applicants",
          offers: { "@type": "Offer", price: "3000", priceCurrency: "INR" },
          areaServed: "Worldwide",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: BasicSupportPage,
});

const includes = [
  {
    icon: MessageSquare,
    title: "2 One-on-One Mock Interviews",
    desc: "Two focused live mocks with a senior Specialist who's cleared the same interview. Real questions, real pacing, real feedback.",
  },
  {
    icon: ClipboardCheck,
    title: "Documentation Assistance",
    desc: "Quick check on what to carry, document order, and the day-of essentials. The basics that nervous applicants forget.",
  },
  {
    icon: Target,
    title: "Line-by-Line Feedback",
    desc: "After each mock you get specific notes - what landed, what sounded rehearsed, what to rephrase. No generic 'you did great'.",
  },
  {
    icon: Headphones,
    title: "WhatsApp Follow-Up",
    desc: "Quick questions between sessions? Drop them on WhatsApp. Your Specialist replies the same day.",
  },
];

const faqs = [
  {
    q: "Is 2 mocks really enough?",
    a: "If you're already confident, your DS-160 is clean, and you have a clear story - yes. Basic is built for applicants who just need a structured rehearsal, not a full prep program. If you're a first-timer or unsure where you stand, pick Full Support instead.",
  },
  {
    q: "How is this different from Full Support?",
    a: "Full Support is end-to-end - 4 mocks plus DS-160 review, profile analysis, and officer-logic strategy (₹4,500). Basic is just 2 mocks plus documentation help (₹3,000). Pick Basic only if you don't need DS-160 review or profile coaching.",
  },
  {
    q: "How quickly can I book?",
    a: "Same-week slots are usually available. Message us on WhatsApp with your interview date and we'll schedule both mocks at times that fit your timeline.",
  },
  {
    q: "Do you record the mocks?",
    a: "By default no - to keep you relaxed. If you want a recording for review, just ask before the session and we'll record and send it to you afterwards.",
  },
  {
    q: "Can I upgrade to Full Support after one mock?",
    a: "Yes. We'll credit what you've paid against the Full Support fee. Just message us on WhatsApp.",
  },
];

function BasicSupportPage() {
  return (
    <>
      {/* Hero */}
      <section className="bg-gradient-hero">
        <div className="mx-auto max-w-5xl px-5 pt-16 pb-12 md:pt-24 md:pb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-50 border border-sky-200 text-sky-900 text-xs font-medium">
            <Target className="h-3.5 w-3.5" /> For confident applicants
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-balance max-w-3xl">
            You&apos;ve got the story. Let&apos;s sharpen the delivery.
          </h1>
          <p className="mt-5 text-lg text-foreground/70 max-w-2xl leading-relaxed">
            Basic Support is two focused mock interviews with a senior Specialist. We replicate the consulate&apos;s pacing and pressure, then give you the specific tweaks that turn a good answer into a sharp one.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => openPlanChat("basic")}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-medium shadow-glow hover:opacity-90 transition"
            >
              Enroll in Basic Support <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">₹3,000 one-time · 2 mocks · Documentation help · Same-week slots</p>
          <FirstSessionPromise variant="inline" className="mt-3" />

        </div>
      </section>

      {/* Why basic works */}
      <section className="mx-auto max-w-5xl px-5 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-medium text-primary uppercase tracking-wider">Why two mocks are enough</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
            Confidence is built by reps, not theory.
          </h2>
          <p className="mt-4 text-foreground/70 leading-relaxed">
            If your DS-160 is clean and your story is clear, what&apos;s left is delivery. Two structured mocks - done well, with sharp feedback - is often the difference between a tight 90-second answer and a rambling one.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            "Real consulate-style pacing and follow-ups",
            "Tighten answers from 60 seconds to a confident 20",
            "Calm body language and natural eye contact",
            "Clear opening line that anchors your story",
            "Sharp answer for 'why this university'",
            "Practiced response to ties-to-home questions",
          ].map((item) => (
            <div key={item} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5">
              <CheckCircle2 className="h-4 w-4 text-sky-600 shrink-0 mt-0.5" />
              <span className="text-sm text-foreground/80">{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* What's included */}
      <section className="bg-muted/30 border-y border-border">
        <div className="mx-auto max-w-5xl px-5 py-16">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-medium text-primary uppercase tracking-wider">What&apos;s included</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
              Lean, focused, and fully 1-on-1.
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {includes.map((it) => (
              <div key={it.title} className="rounded-3xl border border-border bg-card p-6">
                <div className="h-11 w-11 rounded-2xl bg-accent grid place-items-center text-primary">
                  <it.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold">{it.title}</h3>
                <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="mx-auto max-w-5xl px-5 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-medium text-primary uppercase tracking-wider">How it works</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
            From enrollment to interview-ready, in 3 steps.
          </h2>
        </div>
        <ol className="mt-12 grid gap-4 md:grid-cols-3">
          {[
            { n: 1, title: "Quick intake", desc: "Share your interview date, university, consulate, and any answers you're worried about. We tune the mocks accordingly." },
            { n: 2, title: "Mock #1 + feedback", desc: "Realistic mock at consulate pacing. You get a clear written list of what to fix before the next session." },
            { n: 3, title: "Mock #2 + final notes", desc: "Tougher version with curveballs. End with your final delivery checklist for interview day." },
          ].map((step) => (
            <li key={step.n} className="rounded-3xl border border-border bg-card p-6">
              <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground grid place-items-center font-display font-semibold">
                {step.n}
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold">{step.title}</h3>
              <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{step.desc}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Who it's for */}
      <section className="mx-auto max-w-5xl px-5 pb-16">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-3xl border border-border bg-card p-7">
            <div className="inline-flex items-center gap-2 text-emerald-700 text-sm font-semibold">
              <CheckCircle2 className="h-4 w-4" /> This is for you if
            </div>
            <ul className="mt-4 space-y-3 text-sm text-foreground/80">
              {[
                "You're confident in your profile and DS-160",
                "You just want structured practice with feedback",
                "You've done your own research and know your story",
                "Your interview is 1–3 weeks away",
              ].map((i) => (
                <li key={i} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />{i}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-border bg-card p-7">
            <div className="inline-flex items-center gap-2 text-amber-700 text-sm font-semibold">
              <Sparkles className="h-4 w-4" /> Pick a different plan if
            </div>
            <ul className="mt-4 space-y-3 text-sm text-foreground/80">
              {[
                "It's your first US visa interview - go with Full Support",
                "You want DS-160 review and profile analysis - Full Support",
                "You've been refused - Refusal Support is built for you",
                "You want more than 2 mocks of practice",
              ].map((i) => (
                <li key={i} className="flex gap-2"><Sparkles className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />{i}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted/30 border-y border-border">
        <div className="mx-auto max-w-3xl px-5 py-16">
          <div className="text-center">
            <p className="text-sm font-medium text-primary uppercase tracking-wider">FAQ</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Common questions about Basic Support
            </h2>
          </div>
          <div className="mt-10 space-y-4">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-2xl border border-border bg-card p-5">
                <summary className="cursor-pointer list-none font-display text-base font-semibold flex items-center justify-between">
                  {f.q}
                  <span className="ml-4 text-muted-foreground group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-foreground/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink text-ink-foreground">
        <div className="mx-auto max-w-3xl px-5 py-20 text-center">
          <ShieldCheck className="h-10 w-10 text-primary mx-auto" />
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-semibold tracking-tight">
            Two mocks. Sharper answers. A calmer interview.
          </h2>
          <p className="mt-4 text-ink-foreground/70 max-w-xl mx-auto">
            ₹3,000 · 2 one-on-one mocks · Documentation help · Same-week slots. A Specialist reaches out on WhatsApp within 24 hours of enrollment.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => openPlanChat("basic")}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-medium shadow-glow hover:opacity-90 transition"
            >
              Enroll in Basic Support <ArrowRight className="h-4 w-4" />
            </button>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/20 bg-white/[0.04] text-ink-foreground font-medium hover:bg-white/[0.08] transition"
            >
              Compare all plans
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
